/// <amd-module name="Wit/BacklogsHub/Backlog/BacklogViewWrapper" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext } from "VSS/Platform/Layout";
import { Intersection } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";
import { FILTER_RESET_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import * as Resources from "Wit/BacklogsHub/Resources";
import { AgileFilterBar } from "Wit/Common/BacklogContent/AgileFilterBar";
import { BacklogContentView } from "Wit/Common/BacklogContent/BacklogContentView";
import { IWorkItemSelection } from "Wit/Common/BacklogContent/Contracts/Contracts";
import { HubType, IBacklogTree } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { IDirectoryService } from "Wit/DirectoryService/IDirectoryService";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { FilterManager } from "Wit/Filter/FilterManager";

export interface IBacklogViewWrapperProps {
    filter: IFilter;
    filterToggled: IObservableValue<boolean>;
    keepHierarchyWithFilters: IObservableValue<boolean>;
    filterManager: FilterManager;
    toggleParents: () => void;
    onItemSelected?: (selectedItems: IWorkItemSelection[]) => void;
    onMarkTTI: () => void;
}

export function BacklogViewWrapper(props: IBacklogViewWrapperProps) {
    const pageContext = usePageContext();
    const commonBacklogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const backlogsHubService = pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService");
    const isNewSprintsViewEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    const filterResetCallback = async () => {
        if (props.keepHierarchyWithFilters.value) {
            await backlogsHubService.refreshTree({ keepOpen: true, filterManager: props.filterManager });
        }
    };

    React.useEffect(() => {
        props.filter.subscribe(filterResetCallback, FILTER_RESET_EVENT);
        return () => {
            props.filter.unsubscribe(filterResetCallback, FILTER_RESET_EVENT);
        };
    }, [props.keepHierarchyWithFilters, props.filterToggled]);

    return (
        <div className={`${isNewSprintsViewEnabled ? 'backlog-view-wrapper' : 'h-scroll-hidden'} page-content flex-row flex-start full-height`}>
            <Intersection>
                <div className="flex-grow flex-column backlogs-visible-overflow full-height">
                    <div className="page-content-bottom">
                        <Observer backlogTree={commonBacklogContentService.getBacklogTree()}>
                            {(observerProps: { backlogTree: IBacklogTree | undefined }) => {
                                if (observerProps.backlogTree) {
                                    return (
                                        <AgileFilterBar
                                            className={"page-content-top"}
                                            dataSource={commonBacklogContentService}
                                            filter={props.filter}
                                            filterToggled={props.filterToggled}
                                            keepHierarchyWithFilters={props.keepHierarchyWithFilters}
                                            filterManager={props.filterManager}
                                            hub={HubType.Backlogs}
                                            hubService={backlogsHubService}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        </Observer>
                    </div>
                    <BacklogContentView
                        newWorkItemCalloutAnchor={commonBacklogContentService.getNewItemCalloutAnchor()}
                        pageContext={pageContext}
                        directoryDropdownProvider={
                            new DirectoryDropdownProvider({
                                directoryService: pageContext.getService<IDirectoryService>("IBacklogsDirectoryService"),
                                artifactTypeName: Resources.Backlogs,
                                iconProps: { iconName: "BacklogList", ariaHidden: true }
                            })
                        }
                        filterManager={props.filterManager}
                        hub={HubType.Backlogs}
                        toggleFilter={() => {
                            props.filterToggled.value = !props.filterToggled.value;
                        }}
                        toggleParents={props.toggleParents}
                        keepHierarchyWithFilters={props.keepHierarchyWithFilters}
                        onItemSelected={props.onItemSelected}
                        onMarkTTI={props.onMarkTTI}
                    />
                </div>
            </Intersection>
        </div>
    );
}
